/**
 * FlowSettingsDialog — draggable, resizable settings window for the Flow editor.
 *
 * Six tabs: Gameplay · Camera · Interface · Particles · Keys · UI.
 * Deliberately self-contained: no dependency on SR's editorStore, its own
 * sub-components styled to match the broader Synapse design language.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFlowStore } from './flowStore';
import { DEFAULT_FLOW_GAMEPLAY, type FlowSparkShape } from './flowSettings';
import { MAX_SPARKS_PER_HIT } from './flowSparks';
import { useEditorStore } from '../../store/editorStore';
import { BUILTIN_CAMERA } from './flowCameraPresets';
import { getOutputLatency } from './flowAudioEngine';
import FlowCalibrationWizard from './FlowCalibrationWizard';
import FlowSlider from './FlowSlider';
import { KEYBIND_SECTIONS, MOUSE_REFERENCE, comboFromEvent, prettyCombo, isModifierOnly, type FlowAction } from './flowKeybindings';
import { EDIT_WINDOW_BEATS } from './flowSceneConstants';
import { color } from '../../components/ui/theme';
import { IcGamepad, IcWaveBars, IcCamera, IcSparkle, IcKeyboard, IcMonitor, IcClose, IcTriRight, IcTriDown, IcBolt } from '../../components/ui/icons';
import PerformanceSection from '../../components/editor/PerformanceSection';
import { useTheme } from '../../components/ui/themeStore';
import ThemeControls from '../../components/ui/ThemeControls';
import {
  windowStyle,
  titleBarStyle,
  titleInnerStyle,
  closeBtnStyle,
  bodyStyle,
  tabListStyle,
  tabBtnStyle,
  contentStyle,
  tabContentStyle,
  sectionBoxStyle,
  sectionTitleStyle,
  rowStyle,
  rowLabelStyle,
  stepBtn,
  stepBtnSm,
  stepValStyle,
  actionBtnStyle,
  hintTextStyle,
  resetBtnStyle,
  keyRowStyle,
  kbdButtonStyle,
  kbdCapturingStyle,
  keyTdStyle,
  kbdStyle,
  descTdStyle,
  resizeHandleStyle,
} from './FlowSettingsDialog.styles';

// ── Tab definitions ───────────────────────────────────────────────────────────

interface Tab { id: string; label: string; icon: React.ReactNode }
// Tab ORDER mirrors the Synth VR settings dialog (UI · Gameplay · Camera ·
// … · Particles · Keys) so moving between editors doesn't mean re-learning
// where things live — editor-specific tabs (Audio here, Mannequin there) slot
// into the same spine.
const TABS: Tab[] = [
  { id: 'ui',          label: 'UI',         icon: <IcMonitor size={16} /> },
  { id: 'performance', label: 'Perf',       icon: <IcBolt size={16} /> },
  { id: 'gameplay',    label: 'Gameplay',   icon: <IcGamepad size={16} /> },
  { id: 'camera',     label: 'Camera',     icon: <IcCamera size={16} /> },
  { id: 'audio',      label: 'Audio',      icon: <IcWaveBars size={16} /> },
  { id: 'particles',  label: 'Particles',  icon: <IcSparkle size={16} /> },
  { id: 'keys',       label: 'Keys',       icon: <IcKeyboard size={16} /> },
];

// ── Constants ─────────────────────────────────────────────────────────────────

const NOTE_SOUNDS = ['tone', 'clap'] as const;
const NOTE_SOUND_LABELS: Record<string, string> = { tone: 'Tone', clap: 'Clap' };
const SCROLL_SOUNDS = ['classic', 'click', 'soft'] as const;
const SCROLL_SOUND_LABELS: Record<string, string> = { classic: 'Classic', click: 'Click', soft: 'Soft' };
const PLACE_SOUNDS = ['pop', 'blip', 'thock', 'quack', 'none'] as const;
const PLACE_SOUND_LABELS: Record<string, string> = { pop: 'Pop', blip: 'Blip', thock: 'Thock', quack: 'Quack', none: 'Off' };
const DELETE_SOUNDS = ['shatter', 'zap', 'thud', 'none'] as const;
const DELETE_SOUND_LABELS: Record<string, string> = { shatter: 'Shatter', zap: 'Zap', thud: 'Thud', none: 'Off' };

const inlineSfxStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };

/** Compact inline volume slider next to a sound selector — same track/thumb
 *  look as the Mixer's GradientSlider (reads interfaceSettings), just narrower. */
function InlineVol({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const si = useEditorStore(s => s.interfaceSettings);
  const ref = useRef<HTMLDivElement>(null);
  const setFromX = (clientX: number) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    onChange(Math.max(0, Math.min(1, (clientX - r.left) / r.width)));
  };
  const onDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setFromX(e.clientX);
    const mv = (ev: MouseEvent) => { ev.preventDefault(); setFromX(ev.clientX); };
    const up = () => { window.removeEventListener('mousemove', mv); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', mv);
    window.addEventListener('mouseup', up);
  };
  const pct = (value * 100).toFixed(1);
  return (
    <div
      ref={ref}
      onMouseDown={onDown}
      title={`Volume ${Math.round(value * 100)}%`}
      style={{
        position: 'relative', width: 96, height: si.sliderTrackHeight, flexShrink: 0,
        borderRadius: 4, background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
        border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)', cursor: 'pointer',
        userSelect: 'none', WebkitUserSelect: 'none',
      }}
    >
      <div style={{
        position: 'absolute', top: 0, left: 0, height: '100%', width: `${pct}%`,
        borderRadius: 4, background: `linear-gradient(90deg, ${si.sliderGradientFrom}, ${si.sliderGradientTo})`,
      }} />
      <div style={{
        position: 'absolute', top: '50%', left: `${pct}%`, transform: 'translate(-50%, -50%)',
        width: si.sliderThumbSize, height: si.sliderThumbSize, borderRadius: '50%',
        background: si.sliderThumbColor, border: `2px solid ${si.sliderGradientFrom}`,
        boxShadow: `0 0 8px ${si.sliderGradientFrom}80`,
      }} />
    </div>
  );
}

const MIN_W = 480, MIN_H = 380;
const DEFAULT_W = 720, DEFAULT_H = 620;

// ── Drag/resize hook ─────────────────────────────────────────────────────────

function useDragResize(open: boolean) {
  const [pos, setPos] = useState(() => ({
    x: Math.max(20, Math.round((window.innerWidth - DEFAULT_W) / 2)),
    y: Math.max(20, Math.round((window.innerHeight - DEFAULT_H) / 2)),
  }));
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });

  // Re-centre every time the dialog OPENS, using the live viewport. The mount-time
  // measurement can be 0 (the panel mounts during the dashboard→Flow transition
  // before the window is sized), which would otherwise park the window at (20,20)
  // tucked under the sidebar — making the gear look like it "does nothing".
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) {
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      setSize(s => {
        setPos({
          x: Math.max(20, Math.round((vw - s.w) / 2)),
          y: Math.max(20, Math.round((vh - s.h) / 2)),
        });
        return s;
      });
    }
    wasOpen.current = open;
  }, [open]);

  const dragging  = useRef(false);
  const resizing  = useRef(false);
  const dragOff   = useRef({ x: 0, y: 0 });
  const resizeStart = useRef({ mx: 0, my: 0, w: 0, h: 0 });

  const onDragStart = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragging.current = true;
    dragOff.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  }, [pos]);

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    resizing.current = true;
    resizeStart.current = { mx: e.clientX, my: e.clientY, w: size.w, h: size.h };
  }, [size]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (dragging.current) {
        setPos({
          x: Math.max(0, Math.min(window.innerWidth - 80, e.clientX - dragOff.current.x)),
          y: Math.max(0, Math.min(window.innerHeight - 40, e.clientY - dragOff.current.y)),
        });
      }
      if (resizing.current) {
        const dx = e.clientX - resizeStart.current.mx;
        const dy = e.clientY - resizeStart.current.my;
        setSize({
          w: Math.max(MIN_W, resizeStart.current.w + dx),
          h: Math.max(MIN_H, resizeStart.current.h + dy),
        });
      }
    };
    const onUp = () => { dragging.current = false; resizing.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  return { pos, size, onDragStart, onResizeStart };
}

// ── Main dialog ───────────────────────────────────────────────────────────────

interface Props { open: boolean; onClose: () => void }

export default function FlowSettingsDialog({ open, onClose }: Props) {
  // Tab lives in the store so other UI can deep-link into a tab (the shortcut
  // sheet's "Rebind in Settings → Keys" opens the dialog straight on Keys).
  const activeTab = useFlowStore(s => s.settingsTab);
  const setActiveTab = useFlowStore(s => s.setSettingsTab);
  const { pos, size, onDragStart, onResizeStart } = useDragResize(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div style={{
      ...windowStyle,
      // Clamped to the viewport (matches the VR SettingsDialog): the far edges
      // can never leave the screen, whatever the drag/resize state.
      left: `min(${pos.x}px, calc(100vw - 90px))`,
      top: `min(${pos.y}px, calc(100vh - 60px))`,
      width: `min(${size.w}px, calc(100vw - 16px), calc(100vw - ${Math.max(0, pos.x)}px - 8px))`,
      height: `min(${size.h}px, calc(100vh - 16px), calc(100vh - ${Math.max(0, pos.y)}px - 8px))`,
    }}>
      {/* Title bar */}
      <div style={titleBarStyle} onMouseDown={onDragStart}>
        <div style={titleInnerStyle}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          <span>Flow Settings</span>
        </div>
        <button
          style={closeBtnStyle}
          onClick={onClose}
          onMouseDown={e => e.stopPropagation()}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'var(--accent)';
            e.currentTarget.style.borderColor = 'var(--accent)';
            e.currentTarget.style.color = 'oklch(0.14 calc(0.022 * var(--tint-c)) var(--tint-h))';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.55)';
            e.currentTarget.style.color = 'var(--accent)';
          }}
        ><IcClose size={15} /></button>
      </div>

      {/* Body */}
      <div style={bodyStyle}>
        {/* Tab list */}
        <div style={tabListStyle}>
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  ...tabBtnStyle,
                  background: active ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.22), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.12))' : 'transparent',
                  borderColor: active ? 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.45)' : 'transparent',
                  color: active ? color.accent : color.inkFaint,
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {activeTab === 'gameplay'  && <GameplayTab />}
          {activeTab === 'audio'     && <AudioTab />}
          {activeTab === 'camera'    && <CameraTab />}
          {activeTab === 'particles' && <ParticlesTab />}
          {activeTab === 'keys'      && <KeysTab />}
          {activeTab === 'ui'        && <UITab />}
          {activeTab === 'performance' && <PerformanceSection />}
        </div>
      </div>

      {/* Resize handle */}
      <div style={resizeHandleStyle} onMouseDown={onResizeStart}>
        <svg width="12" height="12" viewBox="0 0 12 12" style={{ opacity: 0.4 }}>
          <path d="M11 1L1 11M11 5L5 11M11 9L9 11" stroke="var(--accent-dim)" strokeWidth="1.2" />
        </svg>
      </div>
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function SectionBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={sectionBoxStyle}>
      <div style={sectionTitleStyle}>{title}</div>
      {children}
    </div>
  );
}

/**
 * A SectionBox that starts COLLAPSED — where the granular, rarely-touched
 * knobs live. Users told us the settings felt overwhelming, so each tab now
 * shows the everyday controls first and tucks the fine-tuning behind one of
 * these. Open/closed is remembered per section.
 */
function FineTuning({ id, title = 'Fine tuning', children }: {
  id: string;
  title?: string;
  children: React.ReactNode;
}) {
  const key = `synapse:flow:setsec:${id}`;
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(key) === '1'; } catch { return false; }
  });
  const toggle = () => setOpen(o => {
    const next = !o;
    try { localStorage.setItem(key, next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });
  return (
    <div style={sectionBoxStyle}>
      <div
        style={{ ...sectionTitleStyle, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: open ? undefined : 0 }}
        onClick={toggle}
        title={open ? 'Hide these options' : 'Show the detailed options'}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {open ? <IcTriDown size={10} /> : <IcTriRight size={10} />}
        </span>
        {title}
      </div>
      {open && children}
    </div>
  );
}

/** Horizontal label + content row */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={rowStyle}>
      <span style={rowLabelStyle}>{label}</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

/** Stepper: ‹‹ ‹ VALUE › ›› */
function Stepper({
  value, onDec, onInc, onDecFast, onIncFast,
}: {
  value: string;
  onDec: () => void;
  onInc: () => void;
  onDecFast?: () => void;
  onIncFast?: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {onDecFast && <button style={stepBtnSm} onClick={onDecFast} title="−5">«</button>}
      <button style={stepBtn} onClick={onDec}>◀</button>
      <span style={stepValStyle}>{value}</span>
      <button style={stepBtn} onClick={onInc}>▶</button>
      {onIncFast && <button style={stepBtnSm} onClick={onIncFast} title="+5">»</button>}
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 34, height: 18, borderRadius: 9,
          background: checked ? 'linear-gradient(90deg,var(--accent-dim),var(--accent))' : 'rgba(40,30,70,0.9)',
          border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.35)',
          position: 'relative', cursor: 'pointer', transition: 'background 0.18s',
          flexShrink: 0,
        }}
      >
        <div style={{
          position: 'absolute', top: 2,
          left: checked ? 18 : 2,
          width: 12, height: 12, borderRadius: '50%',
          background: checked ? color.ink : color.inkFaint,
          transition: 'left 0.18s',
        }} />
      </div>
    </label>
  );
}

function ColorSwatch({ value, onChange }: { value: string; onChange: (hex: string) => void }) {
  // The native color input IS the visible chip (styled via .color-swatch), so a
  // direct click opens the browser's colour picker reliably.
  return (
    <input
      className="color-swatch"
      type="color"
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  );
}

function ResetBtn({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} style={resetBtnStyle} title="Reset to defaults">
      ↺ Reset
    </button>
  );
}

// ── Gameplay tab ──────────────────────────────────────────────────────────────

function GameplayTab() {
  const gs      = useFlowStore(s => s.gameplaySettings);
  const setGs   = useFlowStore(s => s.setGameplaySettings);
  const resetGs = useFlowStore(s => s.resetGameplaySettings);

  return (
    <div style={tabContentStyle}>


      <SectionBox title="Note Style">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <ResetBtn onClick={resetGs} />
        </div>
        <FlowSlider label="Scale"     value={gs.noteScale}    min={0.4} max={2.0} step={0.05} onChange={v => setGs({ noteScale: v })} format={v => `${(v*100).toFixed(0)}%`} />
        <FlowSlider
          label="Trail Width"
          value={gs.trailWidth}
          min={0.02}
          max={0.90}
          step={0.01}
          onChange={v => setGs({ trailWidth: v })}
          format={v => v > 0.10 ? `${v.toFixed(2)} ★` : v.toFixed(2)}
        />
        {/* Reviewer perf ask: the rail highlight sweep can be disabled outright;
            when on it only animates within ~3 beats of the editor plane. */}
        <Row label="Rail Sweep VFX">
          <Toggle checked={gs.trailSweepVfx !== false} onChange={v => setGs({ trailSweepVfx: v })} />
        </Row>
      </SectionBox>

      {/* Material / placement micro-adjustments — set once, rarely revisited. */}
      <FineTuning id="gameplay-material" title="Note Material & Height">
        <FlowSlider label="Emissive"  value={gs.noteEmissive} min={0.0} max={1.0} step={0.02} onChange={v => setGs({ noteEmissive: v })} format={v => v.toFixed(2)} />
        <FlowSlider label="Metalness" value={gs.noteMetalness} min={0.0} max={1.0} step={0.02} onChange={v => setGs({ noteMetalness: v })} format={v => v.toFixed(2)} />
        <FlowSlider label="Roughness" value={gs.noteRoughness} min={0.0} max={1.0} step={0.02} onChange={v => setGs({ noteRoughness: v })} format={v => v.toFixed(2)} />
        <FlowSlider
          label="Height"
          value={gs.noteHeight}
          min={-1.5}
          max={1.5}
          step={0.05}
          onChange={v => setGs({ noteHeight: v })}
          format={v => `${v > 0 ? '+' : ''}${v.toFixed(2)}`}
        />
      </FineTuning>

      <SectionBox title="Lane Colors">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <ResetBtn onClick={resetGs} />
        </div>
        {(['Lane 1', 'Lane 2', 'Lane 3', 'Lane 4', 'Lane 5'] as const).map((label, i) => (
          <Row key={i} label={label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ColorSwatch
                value={gs.laneColors[i] ?? DEFAULT_FLOW_GAMEPLAY.laneColors[i]}
                onChange={hex => {
                  const next = [...gs.laneColors];
                  next[i] = hex;
                  setGs({ laneColors: next });
                }}
              />
              <span style={{ fontSize: 10, color: color.inkFaint, fontFamily: 'monospace' }}>
                {(gs.laneColors[i] ?? DEFAULT_FLOW_GAMEPLAY.laneColors[i]).toUpperCase()}
              </span>
            </div>
          </Row>
        ))}
      </SectionBox>
    </div>
  );
}

// ── Audio tab (sound effects + sync/latency) ──────────────────────────────────

function AudioTab() {
  const sfx             = useFlowStore(s => s.sfxSettings);
  const setSfx          = useFlowStore(s => s.setSfxSettings);
  const resetSfx        = useFlowStore(s => s.resetSfxSettings);
  const syncOffsetMs    = useFlowStore(s => s.syncOffsetMs);
  const setSyncOffsetMs = useFlowStore(s => s.setSyncOffsetMs);

  // Auto-detected hardware output latency (refreshed; 0 until audio is ready).
  const [outLatMs, setOutLatMs] = React.useState(0);
  const [calibrating, setCalibrating] = React.useState(false);
  React.useEffect(() => {
    const tick = () => setOutLatMs(Math.round(getOutputLatency() * 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div style={tabContentStyle}>
      <SectionBox title="Sound Effects">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <ResetBtn onClick={resetSfx} />
        </div>
        <Row label="Scroll Sound">
          <div style={inlineSfxStyle}>
            <Stepper
              value={SCROLL_SOUND_LABELS[sfx.scrollSound]}
              onDec={() => { const i = SCROLL_SOUNDS.indexOf(sfx.scrollSound); setSfx({ scrollSound: SCROLL_SOUNDS[(i + SCROLL_SOUNDS.length - 1) % SCROLL_SOUNDS.length] }); }}
              onInc={() => { const i = SCROLL_SOUNDS.indexOf(sfx.scrollSound); setSfx({ scrollSound: SCROLL_SOUNDS[(i + 1) % SCROLL_SOUNDS.length] }); }}
            />
            <InlineVol value={sfx.scrollVolume} onChange={v => setSfx({ scrollVolume: v })} />
          </div>
        </Row>
        {/* Which VOICE the hit uses. "Clap" is the recorded hit from the
            Synths Riders desktop editor (users asked for it by name). */}
        <Row label="Note Hit Sound">
          <div style={inlineSfxStyle}>
            <Stepper
              value={NOTE_SOUND_LABELS[sfx.noteSound ?? 'tone']}
              onDec={() => { const i = NOTE_SOUNDS.indexOf(sfx.noteSound ?? 'tone'); setSfx({ noteSound: NOTE_SOUNDS[(i + NOTE_SOUNDS.length - 1) % NOTE_SOUNDS.length] }); }}
              onInc={() => { const i = NOTE_SOUNDS.indexOf(sfx.noteSound ?? 'tone'); setSfx({ noteSound: NOTE_SOUNDS[(i + 1) % NOTE_SOUNDS.length] }); }}
            />
            <InlineVol value={sfx.noteVolume} onChange={v => setSfx({ noteVolume: v })} />
          </div>
        </Row>
        {/* Per-lane pitches only apply to the synthesised tone. */}
        {(sfx.noteSound ?? 'tone') === 'tone' && (
          <Row label="Note Pitches">
            <Stepper
              value={sfx.laneSoundUniform ? '1 for all' : 'one per lane'}
              onDec={() => setSfx({ laneSoundUniform: !sfx.laneSoundUniform })}
              onInc={() => setSfx({ laneSoundUniform: !sfx.laneSoundUniform })}
            />
          </Row>
        )}
        <Row label="Place Note Sound">
          <div style={inlineSfxStyle}>
            <Stepper
              value={PLACE_SOUND_LABELS[sfx.placeSound]}
              onDec={() => { const i = PLACE_SOUNDS.indexOf(sfx.placeSound); setSfx({ placeSound: PLACE_SOUNDS[(i + PLACE_SOUNDS.length - 1) % PLACE_SOUNDS.length] }); }}
              onInc={() => { const i = PLACE_SOUNDS.indexOf(sfx.placeSound); setSfx({ placeSound: PLACE_SOUNDS[(i + 1) % PLACE_SOUNDS.length] }); }}
            />
            <InlineVol value={sfx.placeVolume} onChange={v => setSfx({ placeVolume: v })} />
          </div>
        </Row>
        <Row label="Delete Note Sound">
          <div style={inlineSfxStyle}>
            <Stepper
              value={DELETE_SOUND_LABELS[sfx.deleteSound]}
              onDec={() => { const i = DELETE_SOUNDS.indexOf(sfx.deleteSound); setSfx({ deleteSound: DELETE_SOUNDS[(i + DELETE_SOUNDS.length - 1) % DELETE_SOUNDS.length] }); }}
              onInc={() => { const i = DELETE_SOUNDS.indexOf(sfx.deleteSound); setSfx({ deleteSound: DELETE_SOUNDS[(i + 1) % DELETE_SOUNDS.length] }); }}
            />
            <InlineVol value={sfx.deleteVolume} onChange={v => setSfx({ deleteVolume: v })} />
          </div>
        </Row>
      </SectionBox>

      <SectionBox title="Sync & Latency">
        <Row label="Output latency (auto)">
          <span style={{ fontFamily: 'monospace', fontSize: 11, color: outLatMs > 0 ? '#34D399' : color.inkFaint }}>
            {outLatMs > 0 ? `${outLatMs} ms` : '— (play to detect)'}
          </span>
        </Row>
        <p style={{ fontSize: 10, color: color.inkFaint, lineHeight: 1.5, margin: '2px 0 6px' }}>
          Hardware output latency is detected and compensated automatically. Use the manual
          fine-tune below only for residual feel, or run the tap calibration.
        </p>
        <FlowSlider
          label="Manual fine-tune"
          value={syncOffsetMs}
          min={-200}
          max={200}
          step={5}
          onChange={v => setSyncOffsetMs(Math.round(v))}
          format={v => `${v > 0 ? '+' : ''}${Math.round(v)} ms`}
        />
        <button style={{ ...actionBtnStyle, width: '100%', marginTop: 8 }} onClick={() => setCalibrating(true)}>
          🥁 Calibrate by tapping
        </button>
      </SectionBox>

      {calibrating && <FlowCalibrationWizard onClose={() => setCalibrating(false)} />}
    </div>
  );
}

// ── Camera tab ────────────────────────────────────────────────────────────────

type V3 = [number, number, number];
const rad2deg = (v: number) => `${(v * 180 / Math.PI).toFixed(1)}°`;
const f1 = (v: number) => v.toFixed(1);

/** Pos X/Y/Z + Rot X/Y/Z sliders for one camera view. Values come from the
 *  saved override or the built-in pose; editing writes the full triple. */
function CameraPoseEditor({ pos, rot, setPos, setRot }: {
  pos: V3; rot: V3; setPos: (p: V3) => void; setRot: (r: V3) => void;
}) {
  const mkPos = (i: 0 | 1 | 2) => (v: number) => { const n: V3 = [...pos]; n[i] = v; setPos(n); };
  const mkRot = (i: 0 | 1 | 2) => (v: number) => { const n: V3 = [...rot]; n[i] = v; setRot(n); };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={camSubLabelStyle}>Position</div>
      <FlowSlider label="Pos X" value={pos[0]} min={-20} max={20} step={0.1} format={f1} onChange={mkPos(0)} />
      <FlowSlider label="Pos Y" value={pos[1]} min={-10} max={30} step={0.1} format={f1} onChange={mkPos(1)} />
      <FlowSlider label="Pos Z" value={pos[2]} min={-80} max={20} step={0.1} format={f1} onChange={mkPos(2)} />
      <div style={camSubLabelStyle}>Rotation</div>
      <FlowSlider label="Rot X" value={rot[0]} min={-1.57} max={1.57} step={0.01} format={rad2deg} onChange={mkRot(0)} />
      <FlowSlider label="Rot Y" value={rot[1]} min={-3.14} max={3.14} step={0.01} format={rad2deg} onChange={mkRot(1)} />
      <FlowSlider label="Rot Z" value={rot[2]} min={-1.57} max={1.57} step={0.01} format={rad2deg} onChange={mkRot(2)} />
    </div>
  );
}

/** Collapsible camera section.
 *
 *  MUST live at module scope: when it was declared inside CameraTab's body a
 *  new component *type* was created on every render, so the first slider
 *  onChange (store write → re-render) unmounted and remounted the whole
 *  subtree. The slider's trackRef died mid-drag and the window mousemove
 *  handler had nothing to measure against — clicking a track jumped once,
 *  dragging did nothing. */
function CamAccordion({ id, title, open, setOpen, children }: {
  id: 'default' | 'sides' | 'custom';
  title: string;
  open: 'default' | 'sides' | 'custom' | null;
  setOpen: (v: 'default' | 'sides' | 'custom' | null) => void;
  children: React.ReactNode;
}) {
  return (
    <div style={sectionBoxStyle}>
      <button style={camAccordionBtnStyle} onClick={() => setOpen(open === id ? null : id)}>
        <span>{open === id ? '▼' : '▶'} {title}</span>
      </button>
      {open === id && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
}

function CameraTab() {
  const setCameraView = useFlowStore(s => s.setCameraView);

  const dPos = useFlowStore(s => s.defaultCameraPosition);
  const dRot = useFlowStore(s => s.defaultCameraRotation);
  const lPos = useFlowStore(s => s.leftCameraPosition);
  const lRot = useFlowStore(s => s.leftCameraRotation);
  const cPos = useFlowStore(s => s.customCameraPosition);
  const cRot = useFlowStore(s => s.customCameraRotation);
  const setDPos = useFlowStore(s => s.setDefaultCameraPosition);
  const setDRot = useFlowStore(s => s.setDefaultCameraRotation);
  const setLPos = useFlowStore(s => s.setLeftCameraPosition);
  const setLRot = useFlowStore(s => s.setLeftCameraRotation);
  const setRPos = useFlowStore(s => s.setRightCameraPosition);
  const setRRot = useFlowStore(s => s.setRightCameraRotation);
  const setCPos = useFlowStore(s => s.setCustomCameraPosition);
  const setCRot = useFlowStore(s => s.setCustomCameraRotation);
  const resetPreset = useFlowStore(s => s.resetCameraPreset);
  const clearCustom = useFlowStore(s => s.clearCustomCamera);

  const [open, setOpen] = React.useState<'default' | 'sides' | 'custom' | null>('default');

  // Editing the Left view mirrors the Right view automatically.
  const setSidesPos = (p: V3) => { setLPos(p); setRPos([-p[0], p[1], p[2]]); };
  const setSidesRot = (r: V3) => { setLRot(r); setRRot([r[0], -r[1], -r[2]]); };

  // Apply a view = make it the live camera (so edits preview immediately).
  const applyView = (v: 'default' | 'left' | 'custom') => setCameraView(v);

  return (
    <div style={tabContentStyle}>
      <p style={hintTextStyle}>
        Adjust position and rotation for each camera view. Changes persist across sessions.
      </p>

      <CamAccordion id="default" title="Default Camera" open={open} setOpen={setOpen}>
        <CameraPoseEditor
          pos={dPos ?? BUILTIN_CAMERA.default.pos} rot={dRot ?? BUILTIN_CAMERA.default.rot}
          setPos={setDPos} setRot={setDRot}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button style={{ ...actionBtnStyle, flex: 1 }} onClick={() => applyView('default')}>Apply Default</button>
          <button style={{ ...resetBtnStyle, flex: 1 }} onClick={() => resetPreset('default')}>↺ Reset</button>
        </div>
      </CamAccordion>

      <CamAccordion id="sides" title="Sides Camera" open={open} setOpen={setOpen}>
        <p style={{ ...hintTextStyle, marginTop: 0 }}>Edit the left side — the right side is mirrored automatically.</p>
        <CameraPoseEditor
          pos={lPos ?? BUILTIN_CAMERA.left.pos} rot={lRot ?? BUILTIN_CAMERA.left.rot}
          setPos={setSidesPos} setRot={setSidesRot}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button style={{ ...actionBtnStyle, flex: 1 }} onClick={() => applyView('left')}>Apply Sides</button>
          <button style={{ ...resetBtnStyle, flex: 1 }} onClick={() => { resetPreset('left'); resetPreset('right'); }}>↺ Reset</button>
        </div>
      </CamAccordion>

      <CamAccordion id="custom" title="Custom Camera" open={open} setOpen={setOpen}>
        <CameraPoseEditor
          pos={cPos ?? BUILTIN_CAMERA.custom.pos} rot={cRot ?? BUILTIN_CAMERA.custom.rot}
          setPos={setCPos} setRot={setCRot}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button style={{ ...actionBtnStyle, flex: 1 }} onClick={() => applyView('custom')}>Apply Custom</button>
          <button style={{ ...resetBtnStyle, flex: 1 }} onClick={() => resetPreset('custom')}>↺ Reset</button>
        </div>
        <button style={{ ...resetBtnStyle, marginTop: 8, width: '100%' }} onClick={clearCustom}>
          ✕ Clear Custom Camera
        </button>
      </CamAccordion>

      <p style={{ ...hintTextStyle, marginTop: 4 }}>
        Tip: switch to <strong>Free Orbit</strong> in the left Camera panel to fly around, then come back here to fine-tune.
      </p>
    </div>
  );
}

const camSubLabelStyle: React.CSSProperties = {
  fontSize: 9, letterSpacing: 1, color: color.accentSoft, textTransform: 'uppercase', fontWeight: 700, marginTop: 2,
};
const camAccordionBtnStyle: React.CSSProperties = {
  width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
  color: '#D9CFff', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: 0,
};

// ── Particles tab ─────────────────────────────────────────────────────────────

const shapeBtnStyle = (on: boolean): React.CSSProperties => ({
  padding: '5px 0', fontSize: 11, fontWeight: 700, cursor: 'pointer',
  borderRadius: 6, letterSpacing: 0.4,
  background: on ? 'linear-gradient(90deg,var(--accent-dim),var(--accent))' : 'rgba(40,30,70,0.9)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.35)',
  color: on ? color.ink : color.inkFaint,
});

function ParticlesTab() {
  const ps    = useFlowStore(s => s.particleSettings);
  const setPs = useFlowStore(s => s.setParticleSettings);
  const reset = useFlowStore(s => s.resetParticleSettings);

  return (
    <div style={tabContentStyle}>
      <SectionBox title="Hit Sparks">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <ResetBtn onClick={reset} />
        </div>
        <Row label="Shape">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
            {(['box', 'cone'] as FlowSparkShape[]).map(sh => (
              <button
                key={sh}
                onClick={() => setPs({ sparkShape: sh })}
                style={shapeBtnStyle(ps.sparkShape === sh)}
              >
                {sh === 'box' ? 'Cube' : 'Cone'}
              </button>
            ))}
          </div>
        </Row>
        <Row label="Count">
          <Stepper
            value={String(ps.sparksPerHit)}
            onDec={() => setPs({ sparksPerHit: Math.max(1, ps.sparksPerHit - 1) })}
            onInc={() => setPs({ sparksPerHit: Math.min(MAX_SPARKS_PER_HIT, ps.sparksPerHit + 1) })}
            onDecFast={() => setPs({ sparksPerHit: Math.max(1, ps.sparksPerHit - 25) })}
            onIncFast={() => setPs({ sparksPerHit: Math.min(MAX_SPARKS_PER_HIT, ps.sparksPerHit + 25) })}
          />
        </Row>
        <FlowSlider
          label="Count"
          value={ps.sparksPerHit}
          min={1} max={MAX_SPARKS_PER_HIT} step={1}
          onChange={v => setPs({ sparksPerHit: Math.round(v) })}
          format={v => String(Math.round(v))}
        />
        <FlowSlider
          label="Lifetime"
          value={ps.sparkLifetime}
          min={0.1} max={2.0} step={0.05}
          onChange={v => setPs({ sparkLifetime: v })}
          format={v => `${v.toFixed(2)}s`}
        />
        {/* Two speeds: every spark picks a random launch speed between them.
            Dragging one past the other is allowed — spawnSparks sorts them. */}
        <FlowSlider
          label="Speed min"
          value={ps.sparkSpeed}
          min={0.5} max={12.0} step={0.1}
          onChange={v => setPs({ sparkSpeed: v })}
          format={v => v.toFixed(1)}
        />
        <FlowSlider
          label="Speed max"
          value={ps.sparkSpeedMax}
          min={0.5} max={12.0} step={0.1}
          onChange={v => setPs({ sparkSpeedMax: v })}
          format={v => v.toFixed(1)}
        />
        <FlowSlider
          label="Turbulence"
          value={ps.sparkTurbulence}
          min={0} max={30} step={0.5}
          onChange={v => setPs({ sparkTurbulence: v })}
          format={v => (v === 0 ? 'off' : v.toFixed(1))}
        />
        {ps.sparkShape === 'box' && (
          <FlowSlider
            label="Spread"
            value={ps.sparkSpread}
            min={0} max={2} step={0.05}
            onChange={v => setPs({ sparkSpread: v })}
            format={v => v.toFixed(2)}
          />
        )}
        <FlowSlider
          label="Gravity"
          value={ps.sparkGravity}
          min={0.0} max={15.0} step={0.5}
          onChange={v => setPs({ sparkGravity: v })}
          format={v => v.toFixed(1)}
        />
        <FlowSlider
          label="Size"
          value={ps.sparkSize}
          min={0.01} max={0.25} step={0.005}
          onChange={v => setPs({ sparkSize: v })}
          format={v => v.toFixed(3)}
        />
      </SectionBox>
    </div>
  );
}

// ── Keys tab (rebindable) ───────────────────────────────────────────────────

function KeysTab() {
  const keybindings    = useFlowStore(s => s.keybindings);
  const setKeybinding  = useFlowStore(s => s.setKeybinding);
  const resetKeys      = useFlowStore(s => s.resetKeybindings);
  const [capturing, setCapturing] = useState<FlowAction | null>(null);

  // While capturing, the next non-modifier keypress becomes the new binding.
  useEffect(() => {
    if (!capturing) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') { setCapturing(null); return; }   // cancel
      if (isModifierOnly(e)) return;                            // wait for a real key
      setKeybinding(capturing, comboFromEvent(e));
      setCapturing(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [capturing, setKeybinding]);

  return (
    <div style={tabContentStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ ...hintTextStyle, margin: 0 }}>
          Click a shortcut, then press the key(s) to rebind it. Esc cancels. Saved per device.
        </p>
        <ResetBtn onClick={resetKeys} />
      </div>

      {KEYBIND_SECTIONS.map(sec => (
        <SectionBox key={sec.title} title={sec.title}>
          {sec.items.map(item => (
            <div key={item.action} style={keyRowStyle}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#E9D5FF', fontWeight: 600 }}>{item.label}</div>
                <div style={{ fontSize: 10, color: '#7d75a8', lineHeight: 1.4 }}>{item.description}</div>
              </div>
              <button
                style={capturing === item.action ? kbdCapturingStyle : kbdButtonStyle}
                onClick={() => setCapturing(item.action)}
              >
                {capturing === item.action ? 'Press keys…' : prettyCombo(keybindings[item.action] || '—')}
              </button>
            </div>
          ))}
        </SectionBox>
      ))}

      <SectionBox title="Mouse (fixed)">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {MOUSE_REFERENCE.map(m => (
              <tr key={m.keys}>
                <td style={keyTdStyle}><kbd style={kbdStyle}>{m.keys}</kbd></td>
                <td style={descTdStyle}>{m.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionBox>
    </div>
  );
}

// ── UI tab ────────────────────────────────────────────────────────────────────

function UITab() {
  const iface    = useFlowStore(s => s.flowInterfaceSettings);
  const setIface = useFlowStore(s => s.setFlowInterfaceSettings);
  const resetI   = useFlowStore(s => s.resetFlowInterfaceSettings);
  const tooltipsEnabled = useTheme(s => s.tooltipsEnabled);
  const setTooltipsEnabled = useTheme(s => s.setTooltipsEnabled);
  const hintBarHidden = useFlowStore(s => s.hintBarHidden);
  const setHintBarHidden = useFlowStore(s => s.setHintBarHidden);
  const editWindowEnabled = useFlowStore(s => s.editWindowEnabled);
  const setEditWindowEnabled = useFlowStore(s => s.setEditWindowEnabled);

  return (
    <div style={tabContentStyle}>
      <SectionBox title="Theme">
        {/* Shared theme panel — identical across Synth VR, Flow and Console. */}
        <ThemeControls />
      </SectionBox>

      <SectionBox title="Interface">
        <Row label="Tooltips">
          <Toggle checked={tooltipsEnabled} onChange={setTooltipsEnabled} />
        </Row>
        {/* The ✕ on the hint bar hides it outright; this brings it back. */}
        <Row label="Shortcut Hint Bar">
          <Toggle checked={!hintBarHidden} onChange={v => setHintBarHidden(!v)} />
        </Row>
        {/* Same switch as the E key — clicks land only near the playhead. */}
        <Row label="Edit Only Near Playhead">
          <Toggle checked={editWindowEnabled} onChange={setEditWindowEnabled} />
        </Row>
        <div style={{ fontSize: 9, lineHeight: 1.4, color: color.inkFaint, marginTop: -2 }}>
          Ignores placement / delete clicks more than {EDIT_WINDOW_BEATS} beats from the playhead (shortcut: E).
        </div>
      </SectionBox>

      <SectionBox title="Highway Elements">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <ResetBtn onClick={resetI} />
        </div>
        <Row label="Beat Numbers">
          <Toggle checked={iface.beatMarkersVisible} onChange={v => setIface({ beatMarkersVisible: v })} />
        </Row>
        <Row label="Strike Bar">
          <Toggle checked={iface.strikeBarVisible} onChange={v => setIface({ strikeBarVisible: v })} />
        </Row>
        <Row label="Note Animations">
          <Toggle checked={iface.noteAnimations} onChange={v => setIface({ noteAnimations: v })} />
        </Row>
        <Row label="Delete Burst VFX">
          <Toggle checked={iface.deleteVfx !== false} onChange={v => setIface({ deleteVfx: v })} />
        </Row>
        {/* Beat/snap lines stay visible during playback by DEFAULT (user
            feedback). This opt-in restores the old fade-out-while-playing. */}
        <Row label="Fade Beat Lines on Play">
          <Toggle checked={iface.fadeBeatLinesOnPlay === true} onChange={v => setIface({ fadeBeatLinesOnPlay: v })} />
        </Row>
        {/* Minimap Zoom lives with the rest of the minimap controls now —
            the ⚙ button in the Minimap panel header (Settings → UI kept
            getting cluttered and the slider was far from what it affects). */}
        <Row label="Trigger Notes Area">
          <Toggle checked={iface.keypadVisible !== false} onChange={v => setIface({ keypadVisible: v })} />
        </Row>
        <Row label="Snap on Pause">
          <Toggle checked={iface.snapOnPause !== false} onChange={v => setIface({ snapOnPause: v })} />
        </Row>
        <Row label="Place Marker">
          <Toggle checked={iface.placeMarkerVisible !== false} onChange={v => setIface({ placeMarkerVisible: v })} />
        </Row>
      </SectionBox>

      {/* Sizes, opacities and the strike-bar colour — dialled in once. */}
      <FineTuning id="ui-highway" title="Highway Fine Tuning">
        <FlowSlider
          label="Strike Bar Width"
          value={iface.strikeBarThickness}
          min={0.02} max={0.4} step={0.01}
          onChange={v => setIface({ strikeBarThickness: v })}
          format={v => v.toFixed(2)}
        />
        <Row label="Strike Bar Accent">
          <Toggle checked={iface.strikeBarUseAccent !== false} onChange={v => setIface({ strikeBarUseAccent: v })} />
        </Row>
        {iface.strikeBarUseAccent === false && (
          <Row label="Strike Bar Color">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ColorSwatch value={iface.strikeBarColor} onChange={hex => setIface({ strikeBarColor: hex })} />
              <span style={{ fontSize: 10, color: color.inkFaint, fontFamily: 'monospace' }}>
                {iface.strikeBarColor.toUpperCase()}
              </span>
            </div>
          </Row>
        )}
        <FlowSlider
          label="Keypad Opacity"
          value={iface.keypadOpacity}
          min={0} max={1} step={0.02}
          onChange={v => setIface({ keypadOpacity: v })}
          format={v => `${(v * 100).toFixed(0)}%`}
        />
        <FlowSlider
          label="Divider Opacity"
          value={iface.dividerOpacity}
          min={0} max={1} step={0.02}
          onChange={v => setIface({ dividerOpacity: v })}
          format={v => `${(v * 100).toFixed(0)}%`}
        />
        <FlowSlider
          label="Place Marker Opacity"
          value={iface.placeMarkerOpacity ?? 0.7}
          min={0} max={1} step={0.02}
          onChange={v => setIface({ placeMarkerOpacity: v })}
          format={v => `${(v * 100).toFixed(0)}%`}
        />
      </FineTuning>

      <FineTuning id="ui-waveform" title="Waveform Visualizer">
        <Row label="Color">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ColorSwatch value={iface.waveformColor} onChange={hex => setIface({ waveformColor: hex })} />
            <span style={{ fontSize: 10, color: color.inkFaint, fontFamily: 'monospace' }}>
              {iface.waveformColor.toUpperCase()}
            </span>
          </div>
        </Row>
        <FlowSlider
          label="Opacity"
          value={iface.waveformOpacity}
          min={0} max={1} step={0.02}
          onChange={v => setIface({ waveformOpacity: v })}
          format={v => `${(v * 100).toFixed(0)}%`}
        />
        <FlowSlider
          label="Density"
          value={iface.waveformDensity}
          min={500} max={10000} step={100}
          onChange={v => setIface({ waveformDensity: v })}
          format={v => `${v.toFixed(0)} bars`}
        />
        <FlowSlider
          label="Height"
          value={iface.waveformHeight}
          min={0.4} max={6} step={0.1}
          onChange={v => setIface({ waveformHeight: v })}
          format={v => v.toFixed(1)}
        />
        <FlowSlider
          label="Distance"
          value={iface.waveformGap}
          min={0} max={4} step={0.1}
          onChange={v => setIface({ waveformGap: v })}
          format={v => v.toFixed(1)}
        />
      </FineTuning>

    </div>
  );
}

